import { isNullish } from '@breezy/shared'
import { createClient } from 'graphql-ws'
import { useEffect, useMemo, useRef } from 'react'
import { subscriptionExchange } from 'urql'
import { getConfig } from '../config'
import type { ConnectivityStatus } from '../providers/ConnectivityProvider'

const config = getConfig()

type GetConnectivityStatus = () => ConnectivityStatus

const getWsClient = (graphQLAccessToken?: string, getStatus?: GetConnectivityStatus) =>
  createClient({
    url: config.querierWsApiUrl,
    // repeat indefinitely
    retryAttempts: Infinity,
    retryWait: async (retries: number) => {
      // retry backoff to 5s, then repeat
      const delayMs = Math.min((retries + 1) * 400, 5000)
      await new Promise(resolve => setTimeout(resolve, delayMs))
    },
    connectionParams: {
      // if an authorization header passed when it's set to jwt auth, it'll
      // infer that the user is unauthenticated and set the hasura role to the HASURA_GRAPHQL_UNAUTHORIZED_ROLE
      // env var we set in our hasura docker container
      // https://hasura.io/docs/latest/auth/authentication/unauthenticated-access/
      headers: !isNullish(graphQLAccessToken)
        ? {
            authorization: `Bearer ${graphQLAccessToken}`,
          }
        : {},
    },
    // Always keep retrying so subscriptions recover after offline/degraded periods.
    shouldRetry: () => true,
  })

const buildSubscriptionExchange = (wsClient: ReturnType<typeof getWsClient>) =>
  subscriptionExchange({
    forwardSubscription: request => {
      return {
        subscribe: sink => {
          const input = { ...request, query: request.query || '' }
          const dispose = wsClient.subscribe(input, sink)

          return {
            unsubscribe: dispose,
          }
        },
      }
    },
  })

export const getUrqlSubscriptionExchange = (graphQLAccessToken?: string, getStatus?: GetConnectivityStatus) => {
  const wsClient = getWsClient(graphQLAccessToken, getStatus)
  return buildSubscriptionExchange(wsClient)
}

export const useUrqlSubscriptionExchange = (graphQLAccessToken?: string, getStatus?: GetConnectivityStatus) => {
  const wsClientRef = useRef<ReturnType<typeof getWsClient>>()

  useEffect(
    () => () => {
      wsClientRef.current?.dispose()
    },
    [],
  )

  return useMemo(() => {
    wsClientRef.current?.dispose()
    const wsClient = getWsClient(graphQLAccessToken, getStatus)
    wsClientRef.current = wsClient
    return buildSubscriptionExchange(wsClient)
  }, [getStatus, graphQLAccessToken])
}
